// note: operators with "matches" included in the name make use of the keyword-search
//       package to perform the expression testing

// todo: review the string operators for consistency with regard to case sensitivity etc.

import { z } from "zod";

// A const object rather than a TS enum: consumers loaded by Node's strip-only
// type stripping (the designer backend) require erasable-only syntax.
export const Operator = {
  EQ: "=",
  NE: "!=",
  LT: "<",
  LTE: "<=",
  GT: ">",
  GTE: ">=",
  Between: "between",
  NotBetween: "!between",
  Has: "has",
  NotHas: "!has",
  Before: "before",
  After: "after",
  First: "first",
  Last: "last",
  Contains: "contains",
  NotContains: "!contains",
  Matches: "matches",
  NotMatches: "!matches",
  MatchesFirst: "matches-first",
  MatchesLast: "matches-last",
  MatchesBefore: "matches-before",
  MatchesAfter: "matches-after",
} as const;

export type Operator = (typeof Operator)[keyof typeof Operator];

// Operator arrays
export const booleanOperators = [Operator.EQ, Operator.NE] as const;

export const numberBinaryOperators = [
  Operator.EQ,
  Operator.NE,
  Operator.LT,
  Operator.LTE,
  Operator.GT,
  Operator.GTE,
] as const;

export const numberTernaryOperators = [Operator.Between, Operator.NotBetween] as const;

export const numberOperators = [...numberBinaryOperators, ...numberTernaryOperators] as const;

export const stringOperators = [
  Operator.EQ,
  Operator.NE,
  Operator.Contains,
  Operator.NotContains,
  Operator.Matches,
  Operator.NotMatches,
] as const;

export const simpleStringOperators = [Operator.EQ, Operator.NE, Operator.Contains, Operator.NotContains] as const;

export const binaryOperators = [
  Operator.EQ,
  Operator.NE,
  Operator.LT,
  Operator.LTE,
  Operator.GT,
  Operator.GTE,
  Operator.Contains,
  Operator.NotContains,
  Operator.Matches,
  Operator.NotMatches,
] as const;

export const ternaryOperators = [
  Operator.Between,
  Operator.NotBetween,
  Operator.Before,
  Operator.After,
  Operator.MatchesBefore,
  Operator.MatchesAfter,
] as const;

export const arrayBinaryOperators = [
  ...binaryOperators,
  Operator.Has,
  Operator.NotHas,
  Operator.First,
  Operator.Last,
  Operator.MatchesFirst,
  Operator.MatchesLast,
] as const;

export const keywordOperators = [Operator.Matches, Operator.NotMatches] as const;

// Zod schemas (derived from arrays)
export const BinaryOperator = z.enum(binaryOperators);
export const TernaryOperator = z.enum(ternaryOperators);
export const BooleanOperator = z.enum(booleanOperators);
export const NumberBinaryOperator = z.enum(numberBinaryOperators);
export const NumberTernaryOperator = z.enum(numberTernaryOperators);
export const NumberOperator = z.union([NumberBinaryOperator, NumberTernaryOperator]);
export const StringOperator = z.enum(stringOperators);
export const SimpleStringOperator = z.enum(simpleStringOperators);
export const ArrayBinaryOperator = z.enum(arrayBinaryOperators);
export const KeywordOperator = z.enum(keywordOperators);

// Inferred types from schemas
export type BinaryOperator = z.infer<typeof BinaryOperator>;
export type TernaryOperator = z.infer<typeof TernaryOperator>;
export type BooleanOperator = z.infer<typeof BooleanOperator>;
export type NumberBinaryOperator = z.infer<typeof NumberBinaryOperator>;
export type NumberTernaryOperator = z.infer<typeof NumberTernaryOperator>;
export type NumberOperator = z.infer<typeof NumberOperator>;
export type StringOperator = z.infer<typeof StringOperator>;
export type SimpleStringOperator = z.infer<typeof SimpleStringOperator>;
export type ArrayBinaryOperator = z.infer<typeof ArrayBinaryOperator>;
export type KeywordOperator = z.infer<typeof KeywordOperator>;

// Helper functions
export const isTernaryOperator = (operator: Operator): operator is TernaryOperator =>
  TernaryOperator.safeParse(operator).success;
